import { useMemo } from 'react';
import type { ComponentType } from 'react';
import type { ColDef, ValueFormatterParams, ICellRendererParams } from 'ag-grid-community';
import type { IHospitalResult } from '../../../models/hospital/HospitalModel';

/**
 * Provides column definitions for the Hospital Management grid.
 */
export function useHospitalManagementColumnDefs(
  actionCellRenderer: ComponentType<ICellRendererParams<IHospitalResult>>
): ColDef<IHospitalResult>[] {
  return useMemo<ColDef<IHospitalResult>[]>(
    () => [
      {
        headerName: 'İşlemler',
        width: 160,
        pinned: 'left',
        sortable: false,
        filter: false,
        cellRenderer: actionCellRenderer,
        cellStyle: () => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 8px',
          backgroundColor: 'transparent',
        }),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 80,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'hospitalName',
        headerName: 'Hastane Adı',
        minWidth: 125,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'createdDate',
        headerName: 'Oluşturulma Tarihi',
        minWidth: 125,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleString('tr-TR');
        },
      },

      {
        field: 'isHospitalClosed',
        headerName: 'Hastane Kapalı',
        width: 120,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) return '-';
          return params.value ? 'Evet' : 'Hayır';
        },
        cellStyle: params => {
          if (params.value === true) {
            return { color: '#dc2626', fontWeight: 'bold' };
          }
          return { color: '#059669', fontWeight: 'bold' };
        },
      },
      {
        field: 'recordCount',
        headerName: 'Kayıt Sayısı',
        width: 120,
        flex: 1,
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: ['equals', 'greaterThan', 'lessThan'],
          defaultOption: 'equals',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) return '0';
          return params.value.toString();
        },
        cellStyle: () => {
          return {
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#1f2937',
          };
        },
      },
      {
        field: 'hospitalCloseDate',
        headerName: 'Kapanış Tarihi',
        minWidth: 150,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '-';
          return new Date(params.value).toLocaleDateString('tr-TR');
        },
      },
    ],
    [actionCellRenderer]
  );
}

/**
 * Provides a default column definition for the Hospital Management grid.
 */
export function useHospitalManagementDefaultColDef(): ColDef<IHospitalResult> {
  return useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
        maxNumConditions: 1,
      },
    }),
    []
  );
}
